export const BUILDS_API_PATH = "/api/builds";
export const READINESS_API_PATH = "/api/readiness";

export type ReadinessCheck = {
  id: string;
  label: string;
  status: "pass" | "warning" | "fail";
  message: string;
};

export type ProvisionerReadiness = {
  ready: boolean;
  message: string;
  checks: ReadinessCheck[];
};

export type BuildStage = {
  id: string;
  label: string;
  state: "pending" | "running" | "succeeded" | "failed" | "skipped";
  message?: string;
};

export type BuildJob = {
  id: string;
  state: "queued" | "running" | "succeeded" | "failed" | "cancelled";
  current_stage: string;
  stages: BuildStage[];
  message: string;
  result?: {
    site_url: string;
    admin_url?: string;
    visual_qa_score?: number;
    visual_qa_threshold?: 84;
    visual_qa_passed?: boolean;
    visual_qa_disposition?: "passed" | "needs_polish";
    visual_qa_critical_issue_count?: number;
    visual_qa_high_issue_count?: number;
    visual_qa_repair_passes_executed?: number;
    visual_qa_noncritical_below_threshold?: boolean;
    visual_qa_summary?: string;
    visual_qa_repairs?: string[];
    image_verify_ok?: boolean;
    image_verify_checked?: number;
    image_verify_issues?: string[];
    video_verify_ok?: boolean;
    video_verify_checked?: number;
    video_verify_issues?: string[];
    polish_followup?: {
      mode: "guarded_recompile_reimport";
      availability: "operator_cli";
      owner_job_id: string;
      suite_shape: "must_match_registered_suite";
    };
  } | null;
};

export type OneTimeCredentials = {
  admin_url: string;
  username: string;
  password: string;
};

export function bearerHeaders(buildAccessKey: string) {
  return { Authorization: `Bearer ${buildAccessKey}` };
}

export function isProvisionerReadiness(value: unknown): value is ProvisionerReadiness {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<ProvisionerReadiness>;
  return typeof candidate.ready === "boolean"
    && typeof candidate.message === "string"
    && Array.isArray(candidate.checks);
}

export function isBuildJob(value: unknown): value is BuildJob {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<BuildJob>;
  return typeof candidate.id === "string"
    && ["queued", "running", "succeeded", "failed", "cancelled"].includes(candidate.state || "")
    && typeof candidate.current_stage === "string"
    && Array.isArray(candidate.stages)
    && typeof candidate.message === "string";
}

export function isOneTimeCredentials(value: unknown): value is OneTimeCredentials {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<OneTimeCredentials>;
  return typeof candidate.admin_url === "string"
    && typeof candidate.username === "string"
    && typeof candidate.password === "string";
}

export function normalizeSiteSlug(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

export function isValidSiteSlug(value: string) {
  return /^(?=.{1,63}$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(value);
}

export function isValidAdminEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function isValidDevLinksDomain(value: string) {
  return /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.dev-links\.com$/i.test(value.trim());
}
